import { useEffect, useState } from "react"
import { useDetectClickOutside } from "react-detect-click-outside"
import styled from "styled-components"
import { BREAKPOINTS, TAP_MIN } from "../GlobalTheme"
import Navigator from "./Navigator"
import Icon from "../Icon"

interface dropdownOption {
  icon?: string,
  label: string,
  color?: string,
  link?: string
}

const OptionsContainer = styled.table<{ $showOptionContainerOnLeft?: boolean, $showOptionContainerOnTop?: boolean, $isDark?: boolean, $width: string }>`
  position: absolute;
  background-color: ${props => props.$isDark ? props.theme.colors.dark : props.theme.colors.white};
  border-radius: 8px;
  box-shadow: 4px 8px 32px 0px #00000033;
  z-index: 100;
  ${props => props.$showOptionContainerOnLeft && {right: '16px'}}
  ${props => props.$showOptionContainerOnTop && {bottom: '48px'}}
  //margin-left: ${props => props.$showOptionContainerOnLeft ? '-110px' : '0px'};
  //transform: translateY(${props => props.$showOptionContainerOnTop ? '-115%' : '0%'});

  ${props => props.$width && {width: props.$width}}

  /* The language picker now lists every translated language, which is far taller than the viewport.
     Without a cap the panel simply runs off the screen and the last entries cannot be reached. */
  display: block;
  max-height: min(60vh, 420px);
  overflow-y: auto;
  overscroll-behavior: contain;

  > tbody {
    display: block;
  }

  /* Without an explicit anchor the panel grows rightward from the trigger's left edge, so the
     right-aligned shell pickers (theme/language) open off-screen. Anchor right on narrow widths. */
  @media (max-width: ${BREAKPOINTS.shell}px) {
    right: 0px;
    left: auto;
    max-width: calc(100vw - 24px);
    box-sizing: border-box;
  }
`

const OptionLabel = styled.span<{ $color?: string }>`
  font-size: 12px;
  margin-left: 5px;
  white-space: nowrap;
  color: ${props => props.$color ?  props.theme.colors[props.$color] || props.$color : props.theme.colors.gray_800};
`

const OptionContainer = styled.div<{ $color?: string }>`
  display: flex;
  cursor: pointer;
  padding: 12px;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    /* Without border-box the 44px minimum is the CONTENT box, so the padding stacks on top and the
       trigger renders 68px tall - an oversized slab next to a one-line label. */
    box-sizing: border-box;
    min-width: ${TAP_MIN}px;
    min-height: ${TAP_MIN}px;
    align-items: center;
  }

  &:hover {
    ${OptionLabel} {
      color: ${props => props.$color ?  props.theme.colors[props.$color] || props.$color : props.theme.colors.gray_600};
    }
  }
`

export default function Dropdown({ title, options, handleChoose, showOptionContainerOnLeft, showOptionContainerOnTop, isDark, width }: { title?: dropdownOption, options: dropdownOption[], handleChoose: (value: string) => void, showOptionContainerOnLeft?: boolean, showOptionContainerOnTop?: boolean, isDark?: boolean, width?: string }) {

  const [selected, setSelected] = useState<dropdownOption>(title ? title : options[0])
  const [isOpen, setIsOpen] = useState<boolean>(false)

  function handleSelect(option: dropdownOption) {
    if (!title) {
      setSelected(option)
    }
    setIsOpen(false)
    handleChoose(option.label)
  }


  useEffect(() => {
    if (title) {
      setSelected(title)
    }
  }, [title])

  return (
    <div ref={useDetectClickOutside({ onTriggered: () => setIsOpen(false) })} style={{ position: 'relative' }}>
      <OptionContainer onClick={() => setIsOpen(!isOpen)} $color={selected.color}>
        {selected.icon && (
          <div style={{ marginRight: "4px" }}>
            <Icon path={selected.icon} color={selected.color} width="16px" height="16px" />
          </div>
        )}
        <OptionLabel $color={selected.color}>{selected.label}</OptionLabel>
      </OptionContainer>
      {isOpen && (
        <OptionsContainer $width={width || ''} $showOptionContainerOnLeft={showOptionContainerOnLeft} $showOptionContainerOnTop={showOptionContainerOnTop} $isDark={isDark}>
          <tbody>
            {options.map(option => (
              <tr key={option.icon + option.label}>
                <td>
                  <OptionContainer $color={option.color} onClick={() => handleSelect(option)}>
                    {option.icon && (
                      <div style={{ marginRight: "4px", marginTop: "4px" }}>
                        <Icon path={option.icon} color={option.color} width="16px" height="16px" />
                      </div>
                    )}
                    {option.label != '' && (
                      <>
                      {option.link ? (
                        <Navigator to={option.link}>
                          <OptionLabel $color={option.color}>{option.label}</OptionLabel>
                        </Navigator>
                      ) : (
                        <a>
                          {/* Need to put it in an a tag because it is still a rendering element for some reason */}
                          <OptionLabel $color={option.color}>{option.label}</OptionLabel>
                        </a>
                      )}
                      </>
                    )}
                  </OptionContainer>
                </td>
              </tr>
            ))}
          </tbody>
        </OptionsContainer>
      )}
    </div>
  )
}